import { component } from 'picoapp';
import noUISlider from 'nouislider';
import CollectionFilter from 'lib/collection-filter';
import Drawer from 'utils/drawer';
import ProductItem from 'lib/product-item';
import formatMoney from 'utils/format-money';
import app from 'app';  // eslint-disable-line

export default component((node, ctx) => {
  /**
   * Data + elements
   */
  const sorting = node.querySelector('.js-sorting');
  const collectionFilter = node.querySelector('.js-collectionFilter');
  const priceFilters = node.querySelectorAll('.js-priceFilter');
  const priceSliderElements = node.querySelectorAll('.js-priceSlider');
  const onSaleFilters = node.querySelectorAll('.js-onSaleFilter');
  const colorFilters = node.querySelectorAll('.js-colorFilter');
  const sizeFilters = node.querySelectorAll('.js-sizeFilter');
  const clearAllBtn = node.querySelector('.js-clearAllBtn');

  const productOutput = node.querySelector('.js-productOutput');
  const viewCount = node.querySelector('.js-viewCount');
  const moreBtn = node.querySelector('.js-moreBtn');

  const filterDrawerElement = node.querySelector('.js-filterDrawer');
  const filterDrawerBtn = node.querySelector('.js-filterDrawerBtn');
  const filterDrawerCloseBtns = node.querySelectorAll('.js-filterDrawerCloseBtn');

  const {
    handle,
    productCount,
    defaultSort,
    pageSize,
    generalCollections,
  } = JSON.parse(node.dataset.config);

  const strings = window.theme.strings.collection;
  const sortTitles = strings.sorting;

  const sortVals = [
    { val: 'featured', title: sortTitles.featured },
    { val: 'title-ascending', title: sortTitles.titleAscending },
    { val: 'title-descending', title: sortTitles.titleDescending },
    { val: 'created-ascending', title: sortTitles.createdAscending },
    { val: 'created-descending', title: sortTitles.createdDescending },
    { val: 'price-ascending', title: sortTitles.priceAscending },
    { val: 'price-descending', title: sortTitles.priceDescending },
  ];

  /**
   * Functions
   */
  const renderSort = (value) => `<option value="${value.val}">${value.title}</option>`;

  const renderCollection = (value, className) => `<li class="collection__collection-filter__item">
    <button type="button" class="${className}" data-value="${value.val}">${value.title}</button>
  </li>`;

  const renderPrice = (min, max, className) => `<input type="hidden" class="${className}" data-min="${min}" data-max="${max}">`;

  const renderOnSale = (value, className) => `<li>
    <button type="button" class="${className}" data-value="${value.val}">
      <div class="flex items-center">
        <div class="${className}__checkbox"></div>
        <span>${value.title}</span>
      </div>
      <div>
        (<span class="px-[2px] js-count"></span>)
      </div>
    </button>
  </li>`;

  const renderFunc = (value, className) => `<li>
    <button type="button" class="${className}" data-value="${value}">
      <div class="flex items-center">
        <div class="${className}__checkbox"></div>
        <span>${value}</span>
      </div>
      <div>
        (<span class="px-[2px] js-count"></span>)
      </div>
    </button>
  </li>`;

  const initPriceFilter = (filter) => {
    let { min } = filter.prices;
    let { max } = filter.prices;

    const priceQuery = filter.queries.find((query) => query.name === 'price');

    if (priceQuery) {
      const values = priceQuery.vals[0].split('-').map((value) => parseInt(value, 10));
      [min, max] = values;
    }

    const priceSliders = [...priceSliderElements].map((element) => noUISlider.create(element, {
      start: [
        min,
        max,
      ],
      range: {
        min: filter.prices.min,
        max: filter.prices.max,
      },
      step: 100,
      connect: [false, true, false],
      tooltips: {
        to: (value) => formatMoney(value, theme.moneyFormat),
        from: (value) => formatMoney(value, theme.moneyFormat),
      },
    }));

    priceSliders.forEach((priceSlider) => {
      priceSlider.on('set', () => {
        // Temporarily store the sliders that have been set
        ctx.hydrate({
          priceSliders: ctx.getState().priceSliders ? [...ctx.getState().priceSliders, priceSlider.target] : [priceSlider.target], // eslint-disable-line
        });

        const values = priceSlider.get().map((value) => parseInt(value, 10));

        priceFilters.forEach((priceFilter) => {
          const input = priceFilter.querySelector('input');

          [input.dataset.min, input.dataset.max] = values;
          input.dispatchEvent(new Event('change'));
        });

        priceSliders.forEach((otherPriceSlider) => {
          // Only set current slider, if it hasn't already been set in the current loop
          // - to avoid infinite loop
          if (!ctx.getState().priceSliders.includes(otherPriceSlider.target)) {
            otherPriceSlider.set(values);
          }
        });

        // Finally, clear the list
        setTimeout(() => {
          ctx.hydrate({ priceSliders: [] });
        }, 100);
      });
    });
  };

  const initClearBtns = (filterInstance) => {
    filterInstance.filters.forEach((filter) => {
      filter.groupEl.forEach((element) => {
        const filterParent = element.closest('.js-filterParent');
        const clearBtn = filterParent?.querySelector('.js-clearFilterBtn');

        if (!clearBtn) {
          return;
        }

        clearBtn.addEventListener('click', () => {
          filterInstance.reset(filter);
        });
      });
    });
  };

  const updateClearBtns = (filterInstance) => {
    filterInstance.filters.forEach((filter) => {
      filter.groupEl.forEach((element) => {
        const filterParent = element.closest('.js-filterParent');
        const clearBtn = filterParent?.querySelector('.js-clearFilterBtn');

        if (!clearBtn) {
          return;
        }

        const hasActiveValues = filterInstance.queries.find((query) => (
          query.name === filter.name
        ));

        clearBtn.hidden = !hasActiveValues;
      });
    });
  };

  const removeFunc = (groupEl) => groupEl.parentElement.parentElement.remove();

  const renderProduct = ({ product }) => `<li class="column column--1/2 xl:column--1/4">
    ${ProductItem(product)}
  </li>`;

  const updateViewCount = (filter) => {
    viewCount.innerHTML = strings.productsViewed
      .replace('%count%', filter.pagination.viewed)
      .replace('%total%', filter.products.filtered.length);

    moreBtn.hidden = filter.pagination.viewed === filter.products.filtered.length;
  };

  const updateValueCounts = (filter) => {
    filter.filters.forEach((f) => {
      const { type } = f;

      if (type === 'option' || type === 'tagPrefix') {
        // We initially check only the first group element,
        // to avoid looping through all products twice
        const items = f.groupEl[0].querySelectorAll(`.${filter.config.classes.item}`);
        const counts = [];

        items.forEach((item) => {
          const { value } = item.dataset;

          counts.push(filter.products.filtered.filter((product) => {
            switch (type) {
              case 'option':
                return product.options.find((option) => option.name.toLowerCase() === f.name.toLowerCase() && option.values.find((val) => val === value)); // eslint-disable-line
              case 'tagPrefix':
                return product.tags.find((tag) => tag === value);
              default:
                return false;
            }
          }).length);
        });

        f.groupEl.forEach((groupEl) => {
          groupEl.querySelectorAll(`.${filter.config.classes.item}`).forEach((item, index) => {
            const count = item.querySelector('.js-count');

            if (count) {
              count.textContent = counts[index];
            }
          });
        });
      }

      // we need to somehow loop through the filters and check each value item to see,
      // how many of the currently filtered products has the value of that item,
      // such that we can write out the count next to the item
    });
  };

  const filters = [
    {
      type: 'sort',
      name: 'sort',
      groupEl: sorting,
      vals: sortVals,
      renderFunc: renderSort,
      removeFunc,
    },
    {
      type: 'price',
      name: 'price',
      groupEl: [...priceFilters],
      renderFunc: [...priceFilters].map(() => renderPrice),
      removeFunc: [...priceFilters].map(() => removeFunc),
    },
    {
      type: 'tagPrefix',
      prefix: 'on_sale_',
      name: 'onSale',
      groupEl: [...onSaleFilters],
      renderFunc: [...onSaleFilters].map(() => renderOnSale),
      removeFunc: [...onSaleFilters].map(() => removeFunc),
    },
    {
      type: 'option',
      name: 'color',
      optionName: 'color',
      groupEl: [...colorFilters],
      renderFunc: [...colorFilters].map(() => renderFunc),
      removeFunc: [...colorFilters].map(() => removeFunc),
    },
    {
      type: 'option',
      name: 'size',
      optionName: 'size',
      groupEl: [...sizeFilters],
      renderFunc: [...sizeFilters].map(() => renderFunc),
      removeFunc: [...sizeFilters].map(() => removeFunc),
    },
  ];

  if (generalCollections.includes(handle)) {
    filters.push({
      type: 'tagPrefix',
      prefix: 'collection_',
      name: 'collection',
      groupEl: collectionFilter,
      renderFunc: renderCollection,
      removeFunc,
    });
  }

  const initFilter = () => {
    const filter = CollectionFilter({ //eslint-disable-line
      collection: {
        handle,
        productCount,
      },
      classes: {
        item: 'collection__filter__item',
        itemActive: 'collection__filter__item--active',
      },
      defaultSort,
      pagination: {
        type: 'button',
        element: moreBtn,
        pageSize,
      },
      render: {
        func: renderProduct,
        output: productOutput,
      },
      prerender: true,
      reset: {
        element: clearAllBtn,
      },
      filters,
    });

    filter.on('renderFilters', (filterInstance) => {
      initPriceFilter(filterInstance);
      initClearBtns(filterInstance);
    });

    filter.on('filterProducts', (filterInstance) => {
      updateValueCounts(filterInstance);
      clearAllBtn.hidden = !filterInstance.queries.length;
      updateClearBtns(filterInstance);
    });

    filter.on('renderProducts', (filterInstance) => {
      updateViewCount(filterInstance);
      app.mount();
    });
  };

  const initFilterDrawer = () => {
    const filterDrawer = Drawer(filterDrawerElement);

    filterDrawerBtn.addEventListener('click', () => filterDrawer.open());

    filterDrawerCloseBtns.forEach((btn) => {
      btn.addEventListener('click', () => filterDrawer.close());
    });
  };

  const init = () => {
    initFilter();
    initFilterDrawer();
  };

  /**
   * Init
   */
  init();
});
